/**
 * OutputCardRenderer — compact evidence/digest/score cards inline in chat.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import type { OutputCardData } from '../types'

interface OutputCardRendererProps {
    card: OutputCardData
}

const CARD_LABELS: Record<string, string> = {
    evidence: 'Evidence',
    digest: 'Digest',
    score: 'Score',
}

function renderEvidence(payload: Record<string, unknown>) {
    const screenshot = payload.screenshot as { thumbnail_url?: string } | undefined
    const summary = typeof payload.summary === 'string' ? payload.summary : ''
    const url = typeof payload.url === 'string' ? payload.url : ''
    return (
        <>
            {screenshot?.thumbnail_url && (
                <Box
                    component="img"
                    src={screenshot.thumbnail_url}
                    alt="Evidence screenshot"
                    sx={{
                        maxWidth: '100%',
                        maxHeight: 120,
                        borderRadius: 1,
                        objectFit: 'cover',
                        mb: 1,
                    }}
                />
            )}
            {summary && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {summary.length > 200 ? `${summary.slice(0, 200)}…` : summary}
                </Typography>
            )}
            {url && (
                <Typography
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ color: 'var(--link-color, #3b82f6)', display: 'block', mt: 0.5 }}
                >
                    {url.slice(0, 60)}… ↗
                </Typography>
            )}
        </>
    )
}

function renderDigest(payload: Record<string, unknown>) {
    const sections = (payload.sections as Array<{ items?: Array<{ urgency?: string }> }>) || []
    const totalItems = sections.reduce((n, s) => n + (s.items?.length || 0), 0)
    const urgentCount = sections.reduce(
        (n, s) => n + (s.items || []).filter(i => i.urgency === 'red').length,
        0,
    )
    const footer = typeof payload.footer === 'string' ? payload.footer : ''
    return (
        <>
            <Typography variant="caption" color="text.secondary">
                {sections.length} sections · {totalItems} items
                {urgentCount > 0 && (
                    <Box component="span" sx={{ color: 'error.main', ml: 1, fontWeight: 600 }}>
                        {urgentCount} urgent
                    </Box>
                )}
            </Typography>
            {footer && (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                    {footer}
                </Typography>
            )}
        </>
    )
}

function renderScore(payload: Record<string, unknown>) {
    const score = (payload.score as { value?: number; max?: number; color?: string; label?: string; trend?: string }) || {}
    const pct = score.max ? Math.round(((score.value || 0) / score.max) * 100) : 0
    const barColor =
        score.color === 'green'
            ? 'var(--success-color, #22c55e)'
            : score.color === 'red'
              ? 'var(--error-color, #ef4444)'
              : 'var(--warning-color, #f59e0b)'
    return (
        <>
            <Box
                sx={{
                    height: 12,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'var(--main-bg, #f0f4f8)',
                    mb: 0.75,
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        width: `${pct}%`,
                        bgcolor: barColor,
                        borderRadius: 1,
                        transition: 'width 400ms ease',
                    }}
                />
            </Box>
            <Typography variant="caption" color="text.secondary">
                {score.value || 0}/{score.max || 100} ({pct}%) {score.label || ''}
                {score.trend && (
                    <Box component="span" sx={{ ml: 0.75 }}>
                        {score.trend === 'up' ? '↑' : score.trend === 'down' ? '↓' : '→'}
                    </Box>
                )}
            </Typography>
        </>
    )
}

export const OutputCardRenderer: React.FC<OutputCardRendererProps> = ({ card }) => {
    const title =
        (card.payload?.title as string) ||
        `${CARD_LABELS[card.card_type] || card.card_type} Card`
    const ts = card.created_at ? new Date(card.created_at).toLocaleString() : ''
    const deepLink = card.sentinel_id
        ? `/sentinel-manager/${card.sentinel_id}/outputs/${card.card_id}`
        : null

    let body: React.ReactNode = null
    switch (card.card_type) {
        case 'evidence':
            body = renderEvidence(card.payload)
            break
        case 'digest':
            body = renderDigest(card.payload)
            break
        case 'score':
            body = renderScore(card.payload)
            break
        default:
            body = null
    }

    return (
        <Box
            sx={{
                border: '1px solid var(--card-border, #e2e8f0)',
                borderRadius: 2,
                p: 1.5,
                bgcolor: 'var(--card-bg, #fff)',
                mt: 1,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    {title}
                </Typography>
                {ts && (
                    <Typography variant="caption" color="text.disabled">
                        {ts}
                    </Typography>
                )}
            </Box>
            {body}
            {deepLink && (
                <Typography
                    component="a"
                    href={deepLink}
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'var(--link-color, #3b82f6)' }}
                >
                    View details →
                </Typography>
            )}
        </Box>
    )
}

export default OutputCardRenderer
